/**
 * KAN-651: карточка варианта-брифа первого сообщения.
 *
 * Бриф (правка и сохранение), переключатель участия в ротации, генерация пула
 * текстов, раскрывающийся список текстов: правка inline, вкл/выкл, удаление, счётчик отправок.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineTextEditor } from '@/components/ui/inline-text-editor';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useGreetingVariantMutations } from '@/hooks/useGreetingVariants';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { GreetingText, GreetingVariant } from '@/services/api/greetingVariants';

export const GENERATE_COUNT = 25;
const TEXT_MAX_LENGTH = 1000;
export const BRIEF_PLACEHOLDER =
  'Например: коротко и по-деловому, спросить, кто отвечает за закупки, упомянуть текущую акцию, без давления.';

type Mutations = ReturnType<typeof useGreetingVariantMutations>;

function TextRow({ item, mutations }: { item: GreetingText; mutations: Mutations }): JSX.Element {
  return (
    <li
      data-testid={`greeting-text-${item.id}`}
      className="flex items-start gap-2 border-b py-1.5 last:border-b-0"
    >
      <Switch
        checked={item.enabled}
        onCheckedChange={(enabled) => mutations.updateText.mutate({ id: item.id, enabled })}
        aria-label="Текст участвует в ротации"
        data-testid={`greeting-text-toggle-${item.id}`}
        className="mt-0.5 scale-75"
      />
      <div className="min-w-0 flex-1">
        <InlineTextEditor
          value={item.text}
          onSave={(text) => mutations.updateText.mutate({ id: item.id, text })}
          testId={`greeting-text-view-${item.id}`}
          inputTestId={`greeting-text-input-${item.id}`}
          errorTestId={`greeting-text-error-${item.id}`}
          ariaLabel="Текст первого сообщения"
          title="Нажмите, чтобы изменить"
          maxLength={TEXT_MAX_LENGTH}
          required
          viewClassName={`text-sm ${item.enabled ? 'text-gray-800' : 'text-gray-400 line-through'}`}
        />
      </div>
      <span
        className="whitespace-nowrap text-xs text-gray-500"
        data-testid={`greeting-text-sent-${item.id}`}
        title="Сколько раз отправлен"
      >
        отправлено: {item.sent_count}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1"
        aria-label="Удалить текст"
        data-testid={`greeting-text-delete-${item.id}`}
        onClick={() => mutations.removeText.mutate(item.id)}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
}

export function GreetingVariantCard({ variant }: { variant: GreetingVariant }): JSX.Element {
  const mutations = useGreetingVariantMutations();
  const [brief, setBrief] = useState(variant.brief);
  const [open, setOpen] = useState(false);
  useEffect(() => setBrief(variant.brief), [variant.brief]);

  const generate = mutations.generate;
  const briefChanged = brief.trim() !== variant.brief.trim();
  const onGenerate = (): void => {
    // Несохранённый бриф сначала сохраняется: генерация идёт по брифу из базы.
    const run = (): void => generate.mutate({ id: variant.id, n: GENERATE_COUNT });
    if (briefChanged) {
      mutations.update.mutate({ id: variant.id, brief }, { onSuccess: run });
    } else {
      run();
    }
  };

  return (
    <div
      data-testid={`greeting-variant-${variant.label}`}
      className="rounded-md border bg-white p-3"
    >
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-gray-900">Вариант {variant.label}</span>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <Switch
            checked={variant.enabled}
            onCheckedChange={(enabled) => mutations.update.mutate({ id: variant.id, enabled })}
            aria-label={`Вариант ${variant.label} участвует в ротации`}
            data-testid={`greeting-variant-toggle-${variant.label}`}
            className="scale-75"
          />
          {variant.enabled ? 'в ротации' : 'выключен'}
        </label>
        <span className="text-xs text-gray-500">
          текстов: {variant.texts_enabled} из {variant.texts.length}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-xs text-gray-500"
          data-testid={`greeting-variant-delete-${variant.label}`}
          onClick={() => {
            if (window.confirm(`Удалить вариант ${variant.label} вместе с текстами?`)) {
              mutations.remove.mutate(variant.id);
            }
          }}
        >
          Удалить вариант
        </Button>
      </div>
      <Textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        placeholder={BRIEF_PLACEHOLDER}
        rows={3}
        aria-label={`Бриф варианта ${variant.label}`}
        data-testid={`greeting-variant-brief-${variant.label}`}
        className="text-sm"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {briefChanged && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`greeting-variant-save-${variant.label}`}
            onClick={() => mutations.update.mutate({ id: variant.id, brief })}
          >
            Сохранить бриф
          </Button>
        )}
        <Button
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={generate.isPending || !brief.trim()}
          data-testid={`greeting-variant-generate-${variant.label}`}
          onClick={onGenerate}
        >
          {generate.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
          {generate.isPending
            ? 'Генерируем, до 2 минут...'
            : `Сгенерировать ${GENERATE_COUNT} текстов`}
        </Button>
        {generate.isSuccess && (
          <span className="text-xs text-green-700" data-testid={`greeting-variant-generated-${variant.label}`}>
            Добавлено текстов: {generate.data.created}
          </span>
        )}
        {generate.isError && (
          <span className="text-xs text-red-700" data-testid={`greeting-variant-error-${variant.label}`}>
            {extractErrorMessage(generate.error, 'Не удалось сгенерировать тексты')}
          </span>
        )}
      </div>
      {variant.texts.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-blue-700"
            data-testid={`greeting-variant-texts-toggle-${variant.label}`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Тексты ({variant.texts.length})
          </button>
          {open && (
            <ul className="mt-1" data-testid={`greeting-variant-texts-${variant.label}`}>
              {variant.texts.map((item) => (
                <TextRow key={item.id} item={item} mutations={mutations} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default GreetingVariantCard;
